/**
 * Word Template Engine — Cover Page Injection
 *
 * Uses the content document (from batchExportToWord) as the base — this preserves
 * all image references, relationships, tables, and formatting intact.
 * The template's cover page sections are extracted and PREPENDED to the content.
 *
 * Flow:
 * 1. docxtemplater fills cover page placeholders ({TITLE}, {DATE}, {AUTHOR}, {BRAND})
 * 2. Cover page XML is extracted from the processed template
 * 3. Cover page relationships (images, hyperlinks) are remapped and copied to content
 * 4. Cover page is prepended to the content document's body
 * 5. Headers/footers from the template are copied into the content document
 * 6. Template media files (logos, images) are copied into the content document
 */

import type { Artefact } from "@/types/artefacts";

import Docxtemplater from "docxtemplater";
import PizZip from "pizzip";

import { logger } from "@/telemetry";

export interface DocxTemplateData {
  title?: string;
  date?: string;
  author?: string;
  brand?: string;
  artefacts: Artefact[];
}

/**
 * Prepare template data from artefacts and options.
 */
export function prepareTemplateData(
  artefacts: Artefact[],
  options: { title?: string; author?: string; brand?: string; locale?: string } = {},
): DocxTemplateData {
  return {
    title: options.title || "Generated Content Export",
    date: new Date().toLocaleDateString(options.locale || "en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    }),
    author: options.author || options.brand || "<Author Name>",
    brand: options.brand || "",
    artefacts,
  };
}

/**
 * Apply a Word template cover page to a pre-built content document.
 *
 * Strategy: the content blob IS the base document (images, tables, formatting all intact).
 * We extract the cover page from the template and prepend it to the content body.
 * We also copy headers/footers from the template for branding.
 *
 * @param templateBuffer - The .docx template as ArrayBuffer
 * @param data - Metadata for cover page placeholders
 * @param contentBlob - Complete .docx from the existing export pipeline
 * @returns Final .docx with template cover page + rich content
 */
export async function applyWordTemplate(
  templateBuffer: ArrayBuffer,
  data: DocxTemplateData,
  contentBlob: Blob,
): Promise<Blob> {
  try {
    // Step 1: Process template with docxtemplater to fill placeholders
    const templateZip = new PizZip(templateBuffer);
    const doc = new Docxtemplater(templateZip, {
      nullGetter: () => "",
      paragraphLoop: true,
      linebreaks: true,
    });

    doc.render({
      TITLE: data.title,
      DATE: data.date,
      AUTHOR: data.author,
      BRAND: data.brand,
      title: data.title,
      date: data.date,
      author: data.author,
      brand: data.brand,
      artefacts: [],
    });

    // Step 2: Extract cover page paragraphs from the processed template
    const processedTemplateZip = doc.getZip();
    const templateDocXml = processedTemplateZip.file("word/document.xml")?.asText();

    if (!templateDocXml) {
      logger.warn("Could not read template document.xml, returning content as-is");

      return contentBlob;
    }

    const templateBodyMatch = templateDocXml.match(/<w:body>([\s\S]*)<\/w:body>/);

    if (!templateBodyMatch) {
      return contentBlob;
    }

    // Remove section properties from template body — content doc has its own
    let coverPageXml = templateBodyMatch[1].replace(/<w:sectPr[\s\S]*?<\/w:sectPr>/g, "");

    // Add a page break after the cover page content
    coverPageXml += '<w:p><w:r><w:br w:type="page"/></w:r></w:p>';

    // Step 3: Open the content document and prepend the cover page
    const contentBuffer = await contentBlob.arrayBuffer();
    const contentZip = new PizZip(contentBuffer);
    const contentDocXml = contentZip.file("word/document.xml")?.asText();

    if (!contentDocXml) {
      logger.warn("Could not read content document.xml, returning content as-is");

      return contentBlob;
    }

    // Step 4: Copy cover page relationships (images, hyperlinks, etc.) with ID remapping
    const templateRels = processedTemplateZip.file("word/_rels/document.xml.rels")?.asText();
    const contentRels = contentZip.file("word/_rels/document.xml.rels")?.asText();
    const rIdMap = new Map<string, string>();

    if (templateRels && contentRels) {
      // Find all relationship IDs referenced in the cover page XML
      const referencedRIds = new Set<string>();
      const rIdPattern = /r:(embed|link|id)="(rId\d+)"/g;
      let rIdMatch;

      while ((rIdMatch = rIdPattern.exec(coverPageXml)) !== null) {
        referencedRIds.add(rIdMatch[2]);
      }

      // Find the highest rId number in the content document to avoid collisions
      const contentRIdNumbers = [...contentRels.matchAll(/Id="rId(\d+)"/g)].map((m) =>
        parseInt(m[1]),
      );
      let nextRId = Math.max(0, ...contentRIdNumbers) + 1;

      // Build rId remapping: old template rId → new unique rId in content
      let newRelEntries = "";

      for (const oldRId of referencedRIds) {
        const relMatch = templateRels.match(
          new RegExp(`<Relationship[^>]*Id="${oldRId}"[^>]*\\/>`),
        );

        if (relMatch) {
          const newRId = `rId${nextRId++}`;

          rIdMap.set(oldRId, newRId);
          newRelEntries += relMatch[0].replace(`Id="${oldRId}"`, `Id="${newRId}"`);
        }
      }

      // Rewrite cover page XML with remapped relationship IDs
      for (const [oldRId, newRId] of rIdMap) {
        coverPageXml = coverPageXml.split(`"${oldRId}"`).join(`"${newRId}"`);
      }

      // Also collect header/footer relationships from template, with ID remapping
      const headerFooterRels = [
        ...templateRels.matchAll(/<Relationship[^>]*Type="[^"]*\/(header|footer)"[^>]*\/>/g),
      ];

      for (const match of headerFooterRels) {
        const relStr = match[0];
        const oldIdMatch = relStr.match(/Id="(rId\d+)"/);

        if (oldIdMatch) {
          const oldId = oldIdMatch[1];
          const newId = `rId${nextRId++}`;

          rIdMap.set(oldId, newId);
          newRelEntries += relStr.replace(`Id="${oldId}"`, `Id="${newId}"`);
        }
      }

      // Write all new relationships into content rels
      if (newRelEntries.length > 0) {
        const updatedRels = contentRels.replace(
          "</Relationships>",
          `${newRelEntries}</Relationships>`,
        );

        contentZip.file("word/_rels/document.xml.rels", updatedRels);
      }
    }

    // Insert cover page right after <w:body> (after remapping so IDs are correct)
    let updatedContentXml = contentDocXml.replace(/<w:body>/, `<w:body>${coverPageXml}`);

    // Inject header/footer references into the content document's section properties
    // so Word knows to use the template's headers and footers
    if (templateRels) {
      let headerFooterRefXml = "";

      for (const match of templateRels.matchAll(
        /<Relationship[^>]*Id="(rId\d+)"[^>]*Type="[^"]*\/(header|footer)"[^>]*\/>/g,
      )) {
        const oldId = match[1];
        const type = match[2]; // "header" or "footer"
        const newId = rIdMap.get(oldId) || oldId;

        if (type === "header") {
          headerFooterRefXml += `<w:headerReference w:type="default" r:id="${newId}"/>`;
        } else {
          headerFooterRefXml += `<w:footerReference w:type="default" r:id="${newId}"/>`;
        }
      }

      if (headerFooterRefXml) {
        // Insert references inside the last <w:sectPr> in the document
        updatedContentXml = updatedContentXml.replace(
          /<w:sectPr([^>]*)>/g,
          (match, attrs, offset) => {
            // Only inject into the LAST sectPr (the document-level one)
            const remaining = updatedContentXml.substring(offset + match.length);

            if (!remaining.includes("<w:sectPr")) {
              return `<w:sectPr${attrs}>${headerFooterRefXml}`;
            }

            return match;
          },
        );
      }
    }

    contentZip.file("word/document.xml", updatedContentXml);

    // Step 5: Copy headers, footers, and their rels from template into the content doc
    const copiedHeaderFooterParts: string[] = [];

    processedTemplateZip.filter((relativePath: string) => {
      if (
        relativePath.startsWith("word/header") ||
        relativePath.startsWith("word/footer") ||
        relativePath.startsWith("word/_rels/header") ||
        relativePath.startsWith("word/_rels/footer")
      ) {
        const fileData = processedTemplateZip.file(relativePath)?.asUint8Array();

        if (fileData) {
          contentZip.file(relativePath, fileData);

          if (!relativePath.includes("_rels")) {
            copiedHeaderFooterParts.push(`/${relativePath}`);
          }
        }
      }

      return false;
    });

    // Register copied header/footer parts in [Content_Types].xml
    if (copiedHeaderFooterParts.length > 0) {
      let contentTypesXml = contentZip.file("[Content_Types].xml")?.asText();

      if (contentTypesXml) {
        for (const partName of copiedHeaderFooterParts) {
          if (!contentTypesXml.includes(`PartName="${partName}"`)) {
            const isHeader = partName.includes("header");
            const ct = isHeader
              ? "application/vnd.openxmlformats-officedocument.wordprocessingml.header+xml"
              : "application/vnd.openxmlformats-officedocument.wordprocessingml.footer+xml";

            contentTypesXml = contentTypesXml.replace(
              "</Types>",
              `<Override PartName="${partName}" ContentType="${ct}"/></Types>`,
            );
          }
        }

        contentZip.file("[Content_Types].xml", contentTypesXml);
      }
    }

    // Step 6: Copy any template media (logos, cover page images, etc.)
    // When a template media file collides with an existing content file (e.g. both have
    // word/media/image1.png), rename the template's copy to avoid overwriting chart images.
    const copiedMediaExtensions = new Set<string>();
    const mediaRenameMap = new Map<string, string>();

    processedTemplateZip.filter((relativePath: string) => {
      if (relativePath.startsWith("word/media/")) {
        const fileData = processedTemplateZip.file(relativePath)?.asUint8Array();

        if (fileData) {
          let targetPath = relativePath;

          if (contentZip.file(relativePath)) {
            // Collision — rename template media file to avoid overwriting content images
            const ext = relativePath.split(".").pop();
            const baseName = relativePath.replace("word/media/", "").replace(`.${ext}`, "");

            targetPath = `word/media/tpl_${baseName}.${ext}`;
            // Track rename so relationship targets can be updated
            mediaRenameMap.set(relativePath.replace("word/", ""), targetPath.replace("word/", ""));
          }

          contentZip.file(targetPath, fileData);
          const ext = targetPath.split(".").pop()?.toLowerCase();

          if (ext) copiedMediaExtensions.add(ext);
        }
      }

      return false;
    });

    // Update relationship targets for any renamed media files
    if (mediaRenameMap.size > 0) {
      let currentRels = contentZip.file("word/_rels/document.xml.rels")?.asText();

      if (currentRels) {
        for (const [oldTarget, newTarget] of mediaRenameMap) {
          currentRels = currentRels.split(`Target="${oldTarget}"`).join(`Target="${newTarget}"`);
        }

        contentZip.file("word/_rels/document.xml.rels", currentRels);
      }
    }

    // Step 7: Ensure [Content_Types].xml includes entries for copied media types
    if (copiedMediaExtensions.size > 0) {
      const contentTypesXml = contentZip.file("[Content_Types].xml")?.asText();

      if (contentTypesXml) {
        const extensionToMime: Record<string, string> = {
          png: "image/png",
          jpg: "image/jpeg",
          jpeg: "image/jpeg",
          gif: "image/gif",
          bmp: "image/bmp",
          tiff: "image/tiff",
          svg: "image/svg+xml",
          emf: "image/x-emf",
          wmf: "image/x-wmf",
        };

        let updatedContentTypes = contentTypesXml;

        for (const ext of copiedMediaExtensions) {
          if (!updatedContentTypes.includes(`Extension="${ext}"`)) {
            const mime = extensionToMime[ext] || `image/${ext}`;

            updatedContentTypes = updatedContentTypes.replace(
              "</Types>",
              `<Default Extension="${ext}" ContentType="${mime}"/></Types>`,
            );
          }
        }

        if (updatedContentTypes !== contentTypesXml) {
          contentZip.file("[Content_Types].xml", updatedContentTypes);
        }
      }
    }

    const output = contentZip.generate({
      type: "blob",
      mimeType: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    });

    logger.info("Word template cover page applied", {
      artefactCount: data.artefacts.length,
      title: data.title,
    });

    return output;
  } catch (error) {
    logger.error("Failed to apply Word template", { error: String(error) });
    throw new Error(
      `Failed to apply Word template: ${error instanceof Error ? error.message : String(error)}`,
    );
  }
}
